import React, { useState, useEffect } from 'react';
import './VacationRequest.css';
import { submitVacationRequest, loadVacationRequests } from './api/vacationRequestsApi';

function calcWorkDays(fromDate, toDate) {
  if (!fromDate || !toDate) return 0;
  const start = new Date(fromDate);
  const end = new Date(toDate);
  if (end < start) return 0;

  let count = 0;
  const d = new Date(start);
  while (d <= end) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function formatDateSK(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function getStatusLabel(status) {
  const labels = {
    'pending': '⏳ Čaká na schválenie',
    'approved': '✓ Schválené',
    'rejected': '✗ Odmietnuté'
  };
  return labels[status] || status;
}

function getStatusColor(status) {
  const colors = {
    'pending': '#FFA500',
    'approved': '#4CAF50',
    'rejected': '#f44336'
  };
  return colors[status] || '#999';
}

export default function VacationRequest({ pin, employeeData }) {
  const [activeTab, setActiveTab] = useState('submit'); // 'submit' or 'history'
  const [form, setForm] = useState({
    dateFrom: '',
    dateTo: '',
    requestType: 'dovolenka',
    notes: ''
  });

  const [workDays, setWorkDays] = useState(0);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle, loading, success, error
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Calculate work days when dates change
  useEffect(() => {
    if (form.dateFrom && form.dateTo) {
      const days = calcWorkDays(form.dateFrom, form.dateTo);
      setWorkDays(days);
    }
  }, [form.dateFrom, form.dateTo]);

  // Load requests when history tab is opened
  useEffect(() => {
    if (activeTab === 'history') {
      loadRequests();
    }
  }, [activeTab]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setSubmitError('');
    setSuccessMessage('');

    if (!form.dateFrom || !form.dateTo) {
      setSubmitStatus('error');
      setSubmitError('Vyplňte prosím obe dátumy');
      return;
    }

    if (workDays <= 0) {
      setSubmitStatus('error');
      setSubmitError('Počet dní musí byť väčší ako 0');
      return;
    }

    const result = await submitVacationRequest(pin, form);

    if (result.ok) {
      setSubmitStatus('success');
      setSuccessMessage('Žiadosť o dovolenku bola úspešne podaná!');
      setForm({
        dateFrom: '',
        dateTo: '',
        requestType: 'dovolenka',
        notes: ''
      });
      setWorkDays(0);
      setTimeout(() => {
        setSubmitStatus('idle');
        setSuccessMessage('');
      }, 3000);
    } else {
      setSubmitStatus('error');
      setSubmitError(result.error || 'Nepodarilo sa podať žiadosť');
    }
  };

  const loadRequests = async () => {
    setLoadingRequests(true);
    const result = await loadVacationRequests(pin);
    if (result.ok) {
      setRequests(result.requests || []);
    }
    setLoadingRequests(false);
  };

  return (
    <div className="vacation-request-container">
      <div className="vacation-header">
        <h2>Žiadosť o dovolenku</h2>
        {employeeData?.meno && <p className="employee-name">Zalogovaný ako: {employeeData.meno}</p>}
      </div>

      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'submit' ? 'active' : ''}`}
          onClick={() => setActiveTab('submit')}
        >
          Podať žiadosť
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          História žiadostí
        </button>
      </div>

      {/* SUBMIT TAB */}
      {activeTab === 'submit' && (
        <form onSubmit={handleSubmit} className="vacation-form">
          <div className="form-group">
            <label htmlFor="dateFrom">Dátum od:</label>
            <input
              type="date"
              id="dateFrom"
              name="dateFrom"
              value={form.dateFrom}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateTo">Dátum do:</label>
            <input
              type="date"
              id="dateTo"
              name="dateTo"
              value={form.dateTo}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="requestType">Typ dovolenky:</label>
              <select
                id="requestType"
                name="requestType"
                value={form.requestType}
                onChange={handleInputChange}
              >
                <option value="dovolenka">Dovolenka</option>
                <option value="nemocenská">Nemocenská</option>
                <option value="iné">Iné</option>
              </select>
            </div>

            <div className="form-group">
              <label>Počet pracovných dní:</label>
              <div className="work-days-display">{workDays} dní</div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Poznámka:</label>
            <textarea
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleInputChange}
              placeholder="Napríklad: Budem v zahraničí..."
              maxLength="500"
              rows="3"
            />
          </div>

          {submitError && <div className="error-message">{submitError}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <button 
            type="submit" 
            className="submit-button"
            disabled={submitStatus === 'loading'}
          >
            {submitStatus === 'loading' ? 'Posielam...' : 'Podať žiadosť'}
          </button>
        </form>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="vacation-history">
          {loadingRequests && <p className="loading">Načítavam žiadosti...</p>}

          {!loadingRequests && requests.length === 0 && (
            <p className="no-requests">Zatiaľ nemáte podané žiadosti</p>
          )}

          {!loadingRequests && requests.length > 0 && (
            <div className="requests-list">
              {requests.map(req => (
                <div key={req.id} className="request-card">
                  <div className="request-header">
                    <span className="request-dates">
                      {formatDateSK(req.date_from)} - {formatDateSK(req.date_to)}
                    </span>
                    <span 
                      className="request-status" 
                      style={{ color: getStatusColor(req.status) }}
                    >
                      {getStatusLabel(req.status)}
                    </span>
                  </div>

                  <div className="request-details">
                    <p><strong>Typ:</strong> {req.request_type}</p>
                    <p><strong>Počet dní:</strong> {req.day_count} pracovných dní</p>
                    {req.notes && <p><strong>Poznámka:</strong> {req.notes}</p>}
                    {req.rejection_reason && (
                      <p className="rejection-reason">
                        <strong>Dôvod odmietnutia:</strong> {req.rejection_reason}
                      </p>
                    )}
                    <p className="request-date">
                      Podané: {new Date(req.created_at).toLocaleDateString('sk-SK')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
